import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import {
  User, Mail, Phone, KeyRound, Shield, CalendarDays, CreditCard, FileText,
  Bell, BellOff, Smartphone, RefreshCw, MessageCircle, Star, Download,
  Trash2, LogOut, AlertTriangle, Loader2,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { useProfile } from "@/hooks/useProfile"
import { useSubscription } from "@/hooks/useSubscription"
import { usePushSubscription } from "@/hooks/usePushSubscription"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useInstallPrompt } from "@/hooks/useInstallPrompt"
import {
  supabase, getStripePrice, exportUserData, requestAccountDeletion,
  type StripePriceRow, type NotificationPreferencesInput,
} from "@/lib/supabase"
import { GENDER_LABELS } from "@/data/labels"
import { MIN_PASSWORD_LENGTH, MIN_PASSWORD_HINT } from "@/lib/passwordRecovery"
import { BottomNav } from "@/components/BottomNav"
import { ScreenHeader, SectionTitle, Card, Row, RowList, Note, Toggle, TONE } from "@/components/profile/ui"
import { EditSheet, SheetInput, SheetChoices } from "@/components/profile/EditSheet"
import { ReviewFlow } from "@/components/profile/ReviewFlow"
import { mailtoHref } from "@/data/contact"
import { findNewerBuild, reloadForBuild } from "@/lib/appUpdate"

type SheetId = "first_name" | "phone" | "password" | "gender" | "birth_date" | "logout" | "delete"

type PrefKey = keyof NotificationPreferencesInput

/**
 * Les cinq préférences de notification, plus « Push actives » en tête, font les six
 * réglages du groupe. Elles vivaient dans l'onglet Notifications, qui redevient un fil
 * pur ; un raccourci y renvoie ici pour ceux qui cherchent à l'ancien endroit.
 */
// Heures : celles des crons (UTC) converties en heure de Paris, été / hiver. Les
// descriptions annonçaient 8h et 7h, et « Rappel de séance » pour la notification
// de FIN de séance (corrigé le 04/10).
// « Bilan de la semaine » n'y figure plus : il a quitté les Réglages le 05/10 (cloche
// seulement), puis n'est plus envoyé du tout depuis le 16/10 — le bilan est à jour en
// continu dans l'app.
const PREFS: { key: PrefKey; label: string; description: string }[] = [
  { key: "session_end",         label: "Fin de séance",             description: "Juste après la fin de ta séance" },
  { key: "post_session_form",   label: "Questionnaire post-séance", description: "30 min après la fin, s'il n'est pas rempli" },
  { key: "hooper_morning",      label: "Bilan du matin",            description: "À 9h (8h en hiver), s'il n'est pas rempli" },
  { key: "weight_reminder",     label: "Rappel pesée",              description: "Chaque lundi à 8h (7h en hiver)" },
  { key: "streak_achievements", label: "Streaks",                   description: "Célébration des séries (5j, 10j…)" },
]

function formatMoney(cents: number, currency: string): string {
  return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: currency.toUpperCase() })
}

function formatLongDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
}

export function SettingsPage() {
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const { profile, loading, updateProfile } = useProfile()
  const { subscription, status, loading: subLoading, openCustomerPortal } = useSubscription()
  const push = usePushSubscription()
  const { track } = useAnalytics()
  const { installed, canPrompt, promptInstall } = useInstallPrompt()

  const [price, setPrice] = useState<StripePriceRow | null>(null)
  const [sheet, setSheet] = useState<SheetId | null>(null)
  const [draft, setDraft] = useState("")
  const [draft2, setDraft2] = useState("")
  const [saving, setSaving] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [updating, setUpdating] = useState(false)

  // La formule affichée est lue depuis le catalogue, jamais écrite en dur : trois autres
  // offres arrivent et ne doivent demander aucun changement de code ici.
  useEffect(() => {
    if (!subscription?.price_id) return
    getStripePrice(subscription.price_id).then(setPrice).catch(() => null)
  }, [subscription?.price_id])

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-[#060a13] flex items-center justify-center">
        <div className="w-9 h-9 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
      </div>
    )
  }

  const raw = profile.raw

  const open = (id: SheetId) => {
    setSheet(id)
    setDraft2("")
    switch (id) {
      case "first_name": setDraft(profile.firstName); break
      case "phone": setDraft(raw.phone ?? ""); break
      case "gender": setDraft(raw.gender); break
      case "birth_date": setDraft(raw.birth_date ?? ""); break
      default: setDraft("")
    }
  }

  const close = () => { if (!saving) setSheet(null) }

  const saveAccountField = async () => {
    if (!sheet || saving) return
    setSaving(true)
    try {
      if (sheet === "password") {
        if (draft.length < MIN_PASSWORD_LENGTH) { toast.error(MIN_PASSWORD_HINT); return }
        if (draft !== draft2) { toast.error("Les deux mots de passe diffèrent"); return }
        const { error } = await supabase.auth.updateUser({ password: draft })
        if (error) { toast.error("Erreur", { description: error.message }); return }
        toast.success("Mot de passe modifié")
        setSheet(null)
        return
      }

      let update: Parameters<typeof updateProfile>[0] = {}
      if (sheet === "first_name") {
        if (!draft.trim()) { toast.error("Prénom requis"); return }
        update = { first_name: draft.trim() }
      } else if (sheet === "phone") {
        update = { phone: draft.trim() || null }
      } else if (sheet === "gender") {
        update = { gender: draft as "male" | "female" }
      } else if (sheet === "birth_date") {
        update = { birth_date: draft || null }
      }

      const { error } = await updateProfile(update)
      if (error) toast.error("Erreur", { description: error })
      else { toast.success("Enregistré"); setSheet(null) }
    } finally {
      setSaving(false)
    }
  }

  const handlePortal = async () => {
    setPortalLoading(true)
    try { await openCustomerPortal() }
    catch (err) { toast.error("Portail indisponible", { description: err instanceof Error ? err.message : undefined }) }
    finally { setPortalLoading(false) }
  }

  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const data = await exportUserData()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `zeyone-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success("Export téléchargé")
    } catch (err) {
      toast.error("Export échoué", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setExporting(false)
    }
  }

  const handleCheckUpdate = async () => {
    if (updating) return
    setUpdating(true)
    try {
      // L'app elle-même d'abord : c'est elle qui change à chaque déploiement, pas le
      // service worker (src/lib/appUpdate.ts).
      const newerBuild = await findNewerBuild()
      if (newerBuild) {
        toast.success("Mise à jour trouvée", { description: "Rechargement…" })
        setTimeout(() => reloadForBuild(newerBuild), 600)
        return
      }
      const reg = await navigator.serviceWorker?.getRegistration()
      if (!reg) { toast.success("Tu es déjà à jour"); return }
      await reg.update()
      const waiting = reg.waiting
      if (waiting) {
        // Le worker en attente ne prend la main qu'après skipWaiting : sur une PWA
        // installée, les onglets ne se ferment jamais tous, la nouvelle version resterait
        // sinon en attente indéfiniment.
        waiting.postMessage({ type: "SKIP_WAITING" })
        toast.success("Mise à jour trouvée", { description: "Rechargement…" })
        setTimeout(() => window.location.reload(), 600)
      } else {
        toast.success("Tu es déjà à jour")
      }
    } catch (err) {
      toast.error("Vérification échouée", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setUpdating(false)
    }
  }

  const handleDelete = async () => {
    if (saving) return
    setSaving(true)
    try {
      await requestAccountDeletion()
      await signOut()
      navigate("/auth", { replace: true })
    } catch (err) {
      toast.error("Suppression échouée", { description: err instanceof Error ? err.message : undefined })
      setSaving(false)
    }
  }

  const handleLogout = async () => {
    if (saving) return
    setSaving(true)
    try {
      await signOut()
      navigate("/auth", { replace: true })
    } catch {
      setSaving(false)
    }
  }

  const prefsDisabled = push.permission === "denied" || !push.isSubscribed
  const prefs = push.preferences

  return (
    <div className="min-h-screen bg-[#060a13] pb-28 cursor-default">
      <ScreenHeader title="Réglages" onBack={() => navigate("/dashboard/profile")} />

      {/* MON COMPTE */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="px-4 pt-2">
        <SectionTitle label="Mon compte" />
        <RowList>
          <Row icon={User} label="Prénom" value={profile.firstName} onClick={() => open("first_name")} />
          {/* E-mail non modifiable ici : c'est l'identifiant de connexion, le changer
              demande une double confirmation par e-mail — un parcours à part entière. */}
          <Row icon={Mail} label="E-mail" value={profile.email} />
          <Row
            icon={Phone} label="Téléphone" sublabel="Pour le suivi WhatsApp"
            value={raw.phone || "Non renseigné"} onClick={() => open("phone")}
          />
          <Row icon={KeyRound} label="Mot de passe" value="Modifier" onClick={() => open("password")} />
          <Row icon={Shield} label="Genre" value={profile.genderLabel} onClick={() => open("gender")} />
          <Row icon={CalendarDays} label="Date de naissance" value={profile.birthDateFR} onClick={() => open("birth_date")} last />
        </RowList>
      </motion.section>

      {/* ABONNEMENT */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }} className="px-4 mt-6">
        <SectionTitle label="Abonnement" />
        <Card>
          <div className="p-4">
            {subLoading ? (
              <p className="text-[12px] text-gray-500 text-center py-4">Chargement…</p>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="w-[42px] h-[42px] rounded-[13px] grid place-items-center shrink-0"
                    style={{ background: TONE.cyan.bg, border: `1px solid ${TONE.cyan.border}`, color: TONE.cyan.fg }}>
                    <CreditCard className="w-5 h-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold text-white truncate">
                      {price?.product_name ?? (status ? "Formule en cours" : "Aucun abonnement")}
                    </p>
                    <p className="text-[12px] text-gray-500 mt-0.5 tnum">
                      {price
                        ? `${formatMoney(price.amount_cents, price.currency)} / ${price.interval === "year" ? "an" : "mois"}`
                        : "—"}
                    </p>
                  </div>
                  <SubscriptionBadge status={status} cancelAtPeriodEnd={subscription?.cancel_at_period_end ?? false} />
                </div>

                {/* L'échec de paiement est l'état le plus important à rendre visible :
                    sans lui, le client perd l'accès sans comprendre pourquoi. */}
                {(status === "past_due" || status === "incomplete") && (
                  <div className="flex items-start gap-2.5 mt-4 p-3 rounded-[14px]"
                    style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.28)" }}>
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-[12px] leading-relaxed" style={{ color: "#fca5a5" }}>
                      {status === "past_due"
                        ? "Ton dernier paiement a échoué. Mets à jour ton moyen de paiement pour ne pas perdre l'accès."
                        : "Ton paiement attend une authentification bancaire. Termine-la pour activer ton accès."}
                    </p>
                  </div>
                )}

                {subscription?.cancel_at_period_end && status === "active" && (
                  <div className="flex items-start gap-2.5 mt-4 p-3 rounded-[14px]"
                    style={{ background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.25)" }}>
                    <AlertTriangle className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
                    <p className="text-[12px] leading-relaxed text-orange-300">
                      Résiliation planifiée — accès garanti jusqu'au {formatLongDate(subscription.current_period_end)}.
                    </p>
                  </div>
                )}

                {/* Pendant la semaine d'essai il n'y a AUCUNE carte enregistrée,
                    donc aucun prélèvement à venir : annoncer « Prochain prélèvement »
                    à cette date était faux, et inquiétant pour qui vient de s'inscrire
                    sans carte. C'est la fin de l'essai, rien d'autre. */}
                <div className="flex justify-between items-center mt-4 pt-4 text-[13px]"
                  style={{ borderTop: "1px solid rgba(30,41,59,0.55)" }}>
                  <span className="text-gray-500">
                    {status === "trialing"
                      ? "Fin de ta semaine d'essai"
                      : subscription?.cancel_at_period_end ? "Fin d'accès" : "Prochain prélèvement"}
                  </span>
                  <strong className="text-white tnum">
                    {formatLongDate(status === "trialing" ? subscription?.trial_ends_at : subscription?.current_period_end)}
                  </strong>
                </div>
                {status === "trialing" && (
                  <p className="text-[11.5px] mt-2 leading-relaxed" style={{ color: "#64748b" }}>
                    Aucune carte enregistrée : rien ne sera prélevé. À la fin de ta semaine,
                    tu recevras ton bilan et tu choisiras la suite.
                  </p>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => navigate("/dashboard/billing")}
                    className="flex-1 py-3 rounded-xl text-[13.5px] font-semibold text-gray-200 cursor-pointer transition-colors hover:bg-white/[0.06]"
                    style={{ border: "1px solid rgba(255,255,255,0.13)", background: "rgba(255,255,255,0.05)" }}
                  >
                    Mes factures
                  </button>
                  <button
                    onClick={handlePortal}
                    disabled={portalLoading || !subscription?.stripe_customer_id}
                    className="flex-1 py-3 rounded-xl text-[13.5px] font-bold cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ background: "#06b6d4", color: "#04222b" }}
                  >
                    {portalLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Gérer mon abonnement
                  </button>
                </div>
              </>
            )}
          </div>
        </Card>
        <Note>
          Résiliation à tout moment, sans justification : elle prend effet à la fin de la période
          payée. Le changement de formule passe par le coach — chaque offre engage un
          accompagnement différent.
        </Note>
      </motion.section>

      {/* NOTIFICATIONS */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="px-4 mt-6">
        <SectionTitle label="Notifications" />
        <RowList>
          {/* « Push actives » n'est pas un réglage mais l'état de la permission navigateur,
              qui a trois valeurs : accordée, refusée, jamais demandée. */}
          {push.state === "denied" ? (
            <Row
              icon={BellOff} label="Notifications bloquées"
              sublabel="Réautorise-les dans les réglages de ton téléphone"
              last={false}
            />
          ) : push.state === "interrupted" ? (
            // Le navigateur garde un abonnement que le serveur n'a plus (le service
            // push l'a refusé) : aucun push ne peut partir. Un appui en recrée un.
            <Row
              icon={AlertTriangle} tone="amber"
              label="Notifications interrompues"
              sublabel="Ton téléphone ne reçoit plus mes rappels"
              last={false}
              right={
                push.subscribing
                  ? <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                  : (
                    <button
                      onClick={() => push.subscribe()}
                      className="text-[12px] font-semibold px-3 py-1.5 rounded-lg cursor-pointer shrink-0"
                      style={{ background: TONE.amber.bg, border: `1px solid ${TONE.amber.border}`, color: TONE.amber.fg }}
                    >
                      Réactiver
                    </button>
                  )
              }
            />
          ) : (
            <Row
              icon={Bell} tone={push.isSubscribed ? "cyan" : "slate"}
              label="Push actives"
              sublabel={push.isSubscribed ? "Reçues même app fermée" : "Pas activées sur cet appareil"}
              right={
                push.subscribing || push.state === "checking"
                  ? <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                  : <Toggle
                      label="Push actives"
                      value={push.isSubscribed}
                      onChange={(v) => (v ? push.subscribe() : push.unsubscribe())}
                    />
              }
            />
          )}

          {PREFS.map((pref, i) => (
            <Row
              key={pref.key}
              icon={Bell}
              tone={prefsDisabled ? "slate" : "cyan"}
              label={pref.label}
              sublabel={pref.description}
              last={i === PREFS.length - 1}
              right={
                <Toggle
                  label={pref.label}
                  disabled={prefsDisabled}
                  value={prefs ? prefs[pref.key] : false}
                  onChange={(v) => {
                    track("push_preference_toggled", { preference: pref.key, value: v })
                    push.updatePreferences({ [pref.key]: v } as NotificationPreferencesInput)
                  }}
                />
              }
            />
          ))}
        </RowList>

        {push.state === "denied" ? (
          <Note>
            Tu as refusé les notifications dans ton navigateur : ces réglages resteront sans
            effet tant que tu ne les réautorises pas dans les paramètres du téléphone
            (Réglages → Notifications → Zeyone, ou l'icône du cadenas dans la barre d'adresse).
          </Note>
        ) : push.state === "interrupted" ? (
          <Note>Réactive les notifications ci-dessus pour que ces réglages prennent effet.</Note>
        ) : !push.isSubscribed ? (
          <Note>Active les push ci-dessus pour que ces réglages prennent effet.</Note>
        ) : (
          <Note>Ces réglages coupent les notifications sur ton téléphone ; tout reste visible dans la cloche.</Note>
        )}
      </motion.section>

      {/* APPLICATION */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="px-4 mt-6">
        <SectionTitle label="Application" />
        <RowList>
          {/* Masquée dès que l'app tourne en mode installé : proposer d'installer ce qui
              l'est déjà est le meilleur moyen de faire douter. */}
          {!installed && (
            <Row
              icon={Smartphone} tone="cyan"
              label="Installer sur l'écran d'accueil"
              sublabel="Plein écran, ouverture directe"
              onClick={async () => {
                // Sur Android la boîte native s'ouvre ; sur iOS, aucun événement
                // n'existe et le geste doit être expliqué.
                if (await promptInstall()) return
                toast.info(
                  canPrompt
                    ? "Installation indisponible pour le moment."
                    : "Sur iPhone : Partager → Sur l'écran d'accueil. Sur Android : menu ⋮ → Installer l'application.",
                  { duration: 8000 }
                )
              }}
            />
          )}
          <Row
            icon={RefreshCw}
            label={`Version ${__APP_VERSION__}`}
            sublabel={updating ? "Vérification…" : "Rechercher une mise à jour"}
            onClick={handleCheckUpdate}
            last
          />
        </RowList>
      </motion.section>

      {/* AIDE */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="px-4 mt-6">
        <SectionTitle label="Aide" />
        <RowList>
          <Row
            icon={MessageCircle} tone="cyan"
            label="Une question ? Écris-moi" sublabel="Je réponds sous 24 h"
            onClick={() => { window.location.href = mailtoHref("Une question") }}
          />
          <Row
            icon={Star} tone="amber"
            label="Partager mon avis" sublabel="2 minutes, ça m'aide beaucoup"
            onClick={() => setReviewOpen(true)}
            last
          />
        </RowList>
      </motion.section>

      {/* CONFIDENTIALITÉ */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="px-4 mt-6">
        <SectionTitle label="Confidentialité et données" />
        <RowList>
          <Row
            icon={Download} label="Exporter mes données"
            value={exporting ? "…" : undefined}
            onClick={handleExport}
          />
          <Row icon={FileText} label="Conditions et confidentialité" onClick={() => navigate("/privacy")} />
          <Row icon={Trash2} label="Supprimer mon compte" danger onClick={() => open("delete")} last />
        </RowList>
      </motion.section>

      {/* DÉCONNEXION — en gris : le rouge est réservé à la suppression de compte. */}
      <div className="px-4 mt-6">
        <button
          onClick={() => open("logout")}
          className="w-full py-4 rounded-[22px] text-[14.5px] font-semibold text-gray-400 cursor-pointer transition-colors hover:text-gray-300 flex items-center justify-center gap-2"
          style={{ background: "rgba(13,20,32,0.92)", border: "1px solid rgba(30,41,59,0.55)" }}
        >
          <LogOut className="w-4 h-4" /> Se déconnecter
        </button>
      </div>

      <p className="text-center text-[10.5px] text-gray-700 mt-6 tnum" style={{ letterSpacing: 1 }}>
        ZEYONE FIT · {new Date(__BUILD_DATE__).toLocaleDateString("fr-FR")}
      </p>

      {/* ── Feuilles ── */}
      <EditSheet open={sheet === "first_name"} title="Prénom" onCancel={close} onSave={saveAccountField} saving={saving}>
        <SheetInput value={draft} onChange={setDraft} />
      </EditSheet>

      <EditSheet
        open={sheet === "phone"} title="Téléphone" onCancel={close} onSave={saveAccountField} saving={saving}
        description="Optionnel. Il sert uniquement au suivi WhatsApp avec ton coach."
      >
        <SheetInput value={draft} onChange={setDraft} type="tel" placeholder="06 12 34 56 78" />
      </EditSheet>

      <EditSheet
        open={sheet === "password"} title="Nouveau mot de passe" onCancel={close} onSave={saveAccountField} saving={saving}
        description={`${MIN_PASSWORD_HINT}.`}
      >
        <div className="flex flex-col gap-2.5">
          <SheetInput value={draft} onChange={setDraft} type="password" placeholder="Nouveau mot de passe" />
          <SheetInput value={draft2} onChange={setDraft2} type="password" placeholder="Confirmer" autoFocus={false} />
        </div>
      </EditSheet>

      <EditSheet
        open={sheet === "gender"} title="Genre" onCancel={close} onSave={saveAccountField} saving={saving}
        description="Il détermine les tables de référence de force et la formule de masse grasse. Le changer régénère ton programme."
      >
        <SheetChoices
          value={draft}
          onChange={setDraft}
          options={Object.keys(GENDER_LABELS).map((id) => ({ id, label: GENDER_LABELS[id] }))}
        />
      </EditSheet>

      <EditSheet open={sheet === "birth_date"} title="Date de naissance" onCancel={close} onSave={saveAccountField} saving={saving}>
        <SheetInput value={draft} onChange={setDraft} type="date" />
      </EditSheet>

      <EditSheet
        open={sheet === "logout"} title="Se déconnecter ?" onCancel={close} onSave={handleLogout}
        saving={saving} saveLabel="Déconnexion"
        description="Tu pourras te reconnecter à tout moment avec le même e-mail."
      >
        <span />
      </EditSheet>

      <EditSheet
        open={sheet === "delete"} title="Supprimer mon compte" onCancel={close} onSave={handleDelete}
        saving={saving} saveLabel="Supprimer" danger
        description="Définitif. Ton programme, tes séances, tes mesures, tes records et tes photos sont effacés et ne peuvent pas être restaurés."
      >
        <div className="p-3 rounded-[14px] text-[12px] leading-relaxed"
          style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#fca5a5" }}>
          Ton abonnement est résilié immédiatement au passage, sans remboursement de la période
          en cours. Pense à exporter tes données avant : après, il n'y a plus rien à exporter.
        </div>
      </EditSheet>

      <ReviewFlow
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        source="settings"
        defaultDisplayName={profile.firstName}
      />

      <BottomNav />
    </div>
  )
}

function SubscriptionBadge({
  status, cancelAtPeriodEnd,
}: { status: string | null; cancelAtPeriodEnd: boolean }) {
  const config: { label: string; color: string } = (() => {
    if (status === "active" && cancelAtPeriodEnd) return { label: "RÉSILIÉ EN FIN DE PÉRIODE", color: "#f97316" }
    if (status === "active") return { label: "ACTIF", color: "#4ade80" }
    if (status === "trialing") return { label: "ESSAI", color: "#06b6d4" }
    if (status === "past_due") return { label: "PAIEMENT EN ÉCHEC", color: "#ef4444" }
    if (status === "incomplete") return { label: "AUTHENTIFICATION REQUISE", color: "#fbbf24" }
    if (status === "canceled") return { label: "RÉSILIÉ", color: "#94a3b8" }
    return { label: "AUCUN", color: "#94a3b8" }
  })()

  return (
    <span
      className="text-[9px] font-bold px-1.5 py-1 rounded-md shrink-0 text-right max-w-[92px] leading-tight"
      style={{ color: config.color, background: `${config.color}1a`, border: `1px solid ${config.color}4d`, letterSpacing: 0.8 }}
    >
      {config.label}
    </span>
  )
}
